/* eslint-disable no-param-reassign */
import { useState, useContext, useEffect } from 'react';
import { Region } from '@water-web/types';

import { cookieSections } from '../cookies';
import { CookieSection, SelectedCookieSections } from '../../../../types';
import { getRegionalDefaults } from '../utils/regionalDefaults';
import { CookieConsentContext } from '../../../../contexts';

const allDenied = cookieSections.reduce((result, section) => {
  result[section.key] = section.isRequired;
  return result;
}, {});

const allAgreed = cookieSections.reduce((result, section) => {
  result[section.key] = true;
  return result;
}, {});

export interface UseSelectedCookieSectionsResult {
  selectedSections: SelectedCookieSections;
  denyAll: () => SelectedCookieSections;
  agreeAll: () => SelectedCookieSections;
  toggleSectionHandlers: (sectionKey: string) => () => SelectedCookieSections;
}

export const useSelectedCookieSections = (region?: Region): UseSelectedCookieSectionsResult => {
  const consent = useContext(CookieConsentContext);
  const [selectedSections, setSelectedSections] = useState(() => {
    return region ? getRegionalDefaults(region) : allDenied;
  });

  useEffect(() => {
    if (consent.initialized && consent.pendingResponse && consent.isManualShow) {
      const currentSelections = {};
      cookieSections.forEach((section) => {
        currentSelections[section.key] = consent.isSectionAccepted(section.key as CookieSection);
      });
      setSelectedSections(currentSelections);
    }
  }, [consent.initialized, consent.pendingResponse, consent.isManualShow, consent.isSectionAccepted]);

  const denyAll = (): SelectedCookieSections => {
    setSelectedSections(allDenied);
    return allDenied;
  };

  const agreeAll = (): SelectedCookieSections => {
    setSelectedSections(allAgreed);
    return allAgreed;
  };

  const toggleSectionHandlers = (sectionKey: string) => (): SelectedCookieSections => {
    const newSelectedSections = {
      ...selectedSections,
      [sectionKey]:
        cookieSections.find((section) => section.key === sectionKey)?.isRequired || !selectedSections[sectionKey],
    };
    setSelectedSections(newSelectedSections);
    return newSelectedSections;
  };

  return {
    selectedSections,
    denyAll,
    agreeAll,
    toggleSectionHandlers,
  };
};
